import { useLayoutEffect, useState } from "react";
import { FaLock } from "react-icons/fa";
import pci from "../assets/pci.png";
import swinkpay_logo from "../assets/swinklogo.png";
import FormatAmount from "../helper/FormatAmount";
import analytics from "../helper/analytics";

const mobilePattern = /^[6-9]\d{9}$/;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Razorpay-style contact capture shown before the payment methods.
// Collects a 10-digit Indian mobile number and an email address.
const ContactDetails = ({ data, onContinue, showCancel, onCancel }) => {
  const [mobile, setMobile] = useState(data?.customerMobile || "");
  const [email, setEmail] = useState(data?.customerEmail || "");
  const [touched, setTouched] = useState({ mobile: false, email: false });

  const isMobileValid = mobilePattern.test(mobile);
  // Email is OPTIONAL (many users in India have no email). Blank is allowed;
  // only validate the format when something has actually been typed.
  const isEmailValid = email === "" || emailPattern.test(email);
  const canContinue = isMobileValid && isEmailValid;

  useLayoutEffect(() => {
    analytics.enterSection("contact");
  }, []);

  const handleMobile = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(digits);
  };

  const handleSubmit = () => {
    if (!canContinue) {
      setTouched({ mobile: true, email: true });
      // log which field blocked continue (no PII — booleans only)
      analytics.track("contact_validation_blocked", {
        mobileValid: isMobileValid,
        emailValid: isEmailValid,
      });
      return;
    }
    analytics.track("contact_continue", { mobileEntered: true, emailEntered: email !== "" });
    analytics.flush("checkpoint_contact"); // safety: contact details committed
    onContinue(mobile, email);
  };

  return (
    <>
      {/* ── Scrollable body ── */}
      <div className="flex-grow min-h-0 w-full overflow-y-auto overflow-x-hidden hide-scrollbar"
           style={{ display: "flex", flexDirection: "column", padding: "16px 16px 8px" }}>

        {/* Heading */}
        <div className="mb-4">
          <h2 className="text-base font-bold text-gray-800 leading-tight">Contact details</h2>
          <p className="text-xs text-gray-400 mt-1 leading-snug">
            We'll send the payment receipt and updates to your mobile.
          </p>
        </div>

        {/* Mobile number */}
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-1">
          Mobile number
        </label>
        <div className="relative mb-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#9ca3af" strokeWidth="1.8">
              <rect x="6" y="2" width="12" height="20" rx="3" />
              <path d="M11 18h2" strokeLinecap="round" />
            </svg>
            <span className="text-sm font-semibold text-gray-500">+91</span>
            <span className="w-px h-4 bg-gray-200" />
          </div>
          <input
            type="tel"
            inputMode="numeric"
            autoFocus
            className="input-modern w-full rounded-xl border bg-white pl-[74px] pr-10 py-2.5 text-sm font-medium placeholder:text-gray-300 text-gray-800"
            style={{ fontSize: 16, borderColor: touched.mobile && !isMobileValid ? "#E80F21" : "#e5e7eb" }}
            placeholder="10-digit mobile number"
            value={mobile}
            onChange={handleMobile}
            onFocus={() => analytics.track("field_focus", { field: "mobile" })}
            onBlur={() => setTouched((t) => ({ ...t, mobile: true }))}
            maxLength={10}
          />
          {mobile && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
                 style={{ background: isMobileValid ? "#1E56C8" : "#f59e0b" }} />
          )}
        </div>
        <div className="min-h-[16px] mb-3 px-1">
          {touched.mobile && !isMobileValid && (
            <p className="text-[11px] text-[#E80F21]">Enter a valid 10-digit mobile number</p>
          )}
        </div>

        {/* Email */}
        <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 px-1">
          Email address <span className="normal-case text-gray-400 font-normal">(optional)</span>
        </label>
        <div className="relative mb-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#9ca3af" strokeWidth="1.8">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <path d="M4 7l8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <input
            type="email"
            className="input-modern w-full rounded-xl border bg-white pl-10 pr-10 py-2.5 text-sm font-medium placeholder:text-gray-300 text-gray-800"
            style={{ fontSize: 16, borderColor: touched.email && !isEmailValid ? "#E80F21" : "#e5e7eb" }}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            onFocus={() => analytics.track("field_focus", { field: "email" })}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            maxLength={60}
          />
          {email && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
                 style={{ background: emailPattern.test(email) ? "#1E56C8" : "#f59e0b" }} />
          )}
        </div>
        <div className="min-h-[16px] mb-3 px-1">
          {touched.email && email !== "" && !isEmailValid && (
            <p className="text-[11px] text-[#E80F21]">Enter a valid email address</p>
          )}
        </div>

        {/* What you'll get — fills the space with purpose */}
        <div className="rounded-2xl overflow-hidden mb-3"
             style={{ background: "#fff", border: "1px solid rgba(30,86,200,0.12)" }}>
          <div className="px-3 pt-2.5 pb-1">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">What you'll get</p>
          </div>
          {[
            {
              title: "Instant payment receipt",
              desc: "Sent to your SMS (and email, if added)",
              icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#1E56C8" strokeWidth="1.7">
                  <path d="M6 2h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1z" strokeLinejoin="round" />
                  <path d="M14 2v5h5M8.5 12h7M8.5 16h7" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              title: "Real-time status alerts",
              desc: "Know the moment your payment succeeds",
              icon: (
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#1E56C8" strokeWidth="1.7">
                  <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9z" strokeLinejoin="round" />
                  <path d="M10.3 21a2 2 0 003.4 0" strokeLinecap="round" />
                </svg>
              ),
            },
          ].map(({ title, desc, icon }, i, arr) => (
            <div key={title}>
              <div className="flex items-center gap-2.5 px-3 py-1.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: "rgba(30,86,200,0.08)" }}>
                  {icon}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-700 leading-tight">{title}</p>
                  <p className="text-[11px] text-gray-400 leading-tight mt-0.5">{desc}</p>
                </div>
              </div>
              {i < arr.length - 1 && <div className="h-px mx-3" style={{ background: "rgba(30,86,200,0.1)" }} />}
            </div>
          ))}
          <div className="pb-1.5" />
        </div>

        {/* Reassurance note */}
        <div className="rounded-xl px-3 py-2.5 flex items-start gap-2"
             style={{ background: "rgba(30,86,200,0.05)", border: "1px solid rgba(30,86,200,0.12)" }}>
          <FaLock className="text-[#1E56C8] flex-shrink-0 mt-0.5" style={{ fontSize: 11 }} />
          <p className="text-[11px] text-gray-500 leading-snug">
            Your details are encrypted and used only for this payment.
          </p>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex-none w-full bg-white border-t border-gray-100">
        <div className="flex items-center justify-center gap-2 py-1.5 px-3">
          <FaLock className="text-[#1E56C8] text-xs" />
          <span className="text-[10px] text-gray-400 font-medium">256-bit Encrypted</span>
          <span className="text-gray-200 text-xs">|</span>
          <img src={pci} alt="pci_logo" className="h-5 w-auto" />
          <span className="text-gray-200 text-xs">|</span>
          <img src={swinkpay_logo} alt="swinkpay_logo" className="h-5 w-auto" />
        </div>
        <div className="flex items-center justify-center p-2 gap-3">
          {showCancel && (
            <button
              type="button"
              onClick={() => { analytics.track("contact_cancel"); onCancel(); }}
              className="text-[#DE554D] text-sm font-semibold border-2 border-[#DE554D] bg-white h-10 rounded-lg flex-1 transition-all duration-200 hover:bg-red-50 active:scale-[0.98] flex items-center justify-center"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canContinue}
            className={
              canContinue
                ? "text-white text-sm font-semibold bg-gradient-to-r from-[#1E56C8] to-[#1340A0] h-10 rounded-lg flex-1 shadow-md transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-1"
                : "text-white text-sm bg-gray-300 h-10 rounded-lg flex-1 flex items-center justify-center cursor-not-allowed"
            }
          >
            Continue to pay ₹{FormatAmount(data?.totalAmount || 0)}
          </button>
        </div>
      </div>
    </>
  );
};

export default ContactDetails;
